import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Space, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import DrawerForm from './DrawerForm';
import TableWithActions from './TableWithActions';

interface ExpenseSectionProps {
  data: any[];
  visible: boolean;
  editingRecord: any;
  onEdit: (record: any) => void;
  onDelete: (key: string) => void;
  onSubmit: (values: any) => void;
  onClose: () => void;
  disabled?: boolean;
}

const { Text } = Typography;

const ExpenseSection: React.FC<ExpenseSectionProps> = ({
  data,
  visible,
  editingRecord,
  onEdit,
  onDelete,
  onSubmit,
  onClose,
  disabled,
}) => {
  const [form] = Form.useForm();

  // Sync form fields with the editing record
  useEffect(() => {
    if (editingRecord) {
      form.setFieldsValue(editingRecord);
    } else {
      form.resetFields();
    }
  }, [editingRecord, form]);

  // Calculate total expense amount
  const totalExpenseAmount = useMemo(() => {
    return data.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [data]);

  const handleSubmit = (values: any) => {
    onSubmit(values); // Delegate submission logic to parent
    form.resetFields();
  };

  return (
    <div>
      <Space
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h3>Expenses</h3>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={() => onEdit(null)}
          disabled={disabled}
        >
          Add Expense
        </Button>
      </Space>
      <TableWithActions
        columns={[
          { title: 'Expense Category', dataIndex: 'expenseCategory', key: 'expenseTitle' },
          { title: 'Note', dataIndex: 'note', key: 'note' },
          { title: 'Amount', dataIndex: 'amount', key: 'amount' },
        ]}
        data={data}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {/* Total Expense Amount */}
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Text strong style={{ fontSize: 16 }}>
          Total Expense Amount: {totalExpenseAmount.toFixed(2)}
        </Text>
      </div>
      <DrawerForm
        visible={visible}
        title={editingRecord ? 'Edit Expense' : 'Add Expense'}
        form={form}
        onClose={onClose}
        onSubmit={handleSubmit}
        fields={[
          { name: 'expenseCategory', label: 'Expense Category', type: 'input' },
          { name: 'note', label: 'Note', type: 'textarea' },
          { name: 'amount', label: 'Amount', type: 'number', rules: [{ required: true }] },
        ]}
      />
    </div>
  );
};

export default ExpenseSection;
